import Cookies from 'js-cookie';
import { isEmpty } from 'lodash';

import apiGet from '@api/api-get';
import apiPost from '@api/api-post';
import apiDelete from '@api/api-delete';
import apiPut from '@api/api-put';

interface IResult {
  code: number,
  msg: string,
  result: any,
}

const HRService = {
  // 初始化用户筛选项
  initUserOption: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/user_option/initial');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 获取用户筛选项
  getUserOption: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/user_option');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 编辑用户筛选项
  editUserOption: async (params: any) => {
    // my_industry: 用户所在行业
    // multi_cities: 用户多选城市
    // multi_industries: 用户多选行业
    // standard_city: 基准城市
    const data: IResult | null = await apiPut('/mg/hr_indicator/user_option', params);
    return data;
  },
  // 涨薪率 - 行业涨薪率
  upRateIndustry: async (industryList?: string[]) => {
    let params = null;
    if (industryList && industryList.length > 0) {
      params = { industryList };
    }
    const data: IResult | null = await apiGet('/mg/hr_indicator/salary_change/multi_industry', params);
    if (!data) return null;
    return (data as IResult).result;
  },
  // 涨薪率 - 涨薪率时间趋势
  upRateIndustryTime: async (industry?: string) => {
    let params = null;
    if (industry) {
      params = { industry };
    }
    const data: IResult | null = await apiGet('/mg/hr_indicator/salary_change/year_trend', params);
    if (!data) return null;
    return (data as IResult).result;
  },
  // 涨薪率 - 城市涨薪率
  upRateCity: async (industry?: string, cityList?: string[]) => {
    let params: any = {};

    if (industry) {
      params.industry = industry;
    }

    if (cityList && cityList.length > 0) {
      params.cityList = cityList;
    }

    // If no parameters were provided, set params to null
    if (Object.keys(params).length === 0) {
      params = null;
    }

    const data: IResult | null = await apiGet('/mg/hr_indicator/salary_change/multi_city', params);
    if (!data) return null;
    return (data as IResult).result;
  },
  // 涨薪率 - 职能类型涨薪率
  upRateCategoryType: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/salary_change/function_type');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 涨薪率 - 细分职能涨薪率
  upRateCategoryDetail: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/salary_change/segment_category');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 离职率 - 行业离职率
  dimissionIndustry: async (year: number | string) => {
    const data: IResult | null = await apiGet(`/mg/hr_indicator/dimission_rate/multi_industry?year=${year}`);
    if (!data) return null;
    return (data as IResult).result;
  },
  // 离职率 - 行业离职率(全部行业)
  dimissionIndustryAll: async (industryList?: string[]) => {
    let params = null;
    if (industryList && industryList.length > 0) {
      params = { industryList };
    }
    const data: IResult | null = await apiGet('/mg/hr_indicator/dimission_rate/multi_industry/all_year', params);
    if (!data) return null;
    return (data as IResult).result;
  },
  // 离职率 - 离职率年份选项
  dismissionYearOption: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/dimission_rate/year_options');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 离职率 - 离职率时间趋势
  dismissionTimeTrend: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/dimission_rate/year_trend');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 行业应届生起薪
  freshIndustry: async (year: number | string) => {
    const data: IResult | null = await apiGet(`/mg/hr_indicator/fresh_graduate/multi_industry?year=${year}`);
    if (!data) return null;
    return (data as IResult).result;
  },
  // 行业应届生起薪 - 年份选项
  freshIndustryYear: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/fresh_graduate/year_options');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 职能应届生起薪
  freshCategory: async (year: number | string, industry: string = '') => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/fresh_graduate/category', {
      year,
      industry,
    });
    if (!data) return null;
    return (data as IResult).result;
  },
  // 年度应届生起薪
  yearFreshCategory: async (industry: string = '') => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/fresh_graduate/year_trend', {
      industry,
    });
    if (!data) return null;
    return (data as IResult).result;
  },
  // 城市应届生起薪
  cityFreshCategory: async (year: number | string, industry: string = '') => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/fresh_graduate/multi_city', {
      year,
      industry,
    });
    if (!data) return null;
    return (data as IResult).result;
  },
  // 城市薪酬差异系数
  cityDiff: async (year: number | string, industry: string = '') => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/city_difference', {
      year,
      industry
    });
    if (!data) return null;
    return (data as IResult).result;
  },
  // 城市薪酬差异系数 - 年份选项
  cityDiffYear: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/city_difference/year_options');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 概览描述
  overview: async (industryName: string) => {
    const data: IResult | null = await apiGet(`/mg/hr_indicator/recruitment/overview?industry=${industryName}`);
    if (!data) return null;
    return (data as IResult).result;
  },
  // 涨薪率城市选项列表
  salaryCityOption: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/salary_change/city_options');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 应届生起薪城市选项列表
  freshCityOption: async () => {
    const data: IResult | null = await apiGet('/mg/hr_indicator/fresh_graduate/city_options');
    if (!data) return null;
    return (data as IResult).result;
  },
  // 热门岗位及薪酬数据
  hotPosition: async (industryList?: string[]) => {
    let params = null;
    if (industryList && industryList.length > 0) {
      params = { industryList };
    }
    const data: IResult | null = await apiGet('/mg/hr_indicator/recruitment/hot_position', params);
    if (!data) return null;
    return (data as IResult).result;
  },
};

export default HRService;
